/**
 * Normaliza cores modernas para o formato legado antes da captura html2canvas.
 *
 * O html2canvas 1.4.1 só parseia cores legadas (hex/rgb/hsl) e explode com
 * 'Attempting to parse an unsupported color function "color"' em qualquer
 * outra. O Chrome computa `color-mix(in srgb, var(--accent) 22%, transparent)`
 * como `color(srgb 0.97 0.45 0.08 / 0.22)` — foi o que derrubou a exportação
 * de imagem do Planejamento de Entregas (chip do marceneiro,
 * PlanejamentoEntregas/styles.jsx). E color-mix é idiomático neste codebase
 * (17 arquivos): proibir seria enxugar gelo. Em vez disso, na hora da captura
 * todo valor computado com função moderna vira o equivalente rgba(), aplicado
 * como estilo inline; depois da captura, restaura.
 *
 * A conversão usa o próprio browser: pinta 1×1 pixel num canvas sRGB e lê os
 * bytes com getImageData. NÃO dá para confiar no getter de fillStyle — o
 * Chrome moderno preserva color(srgb ...) em vez de serializar para legado
 * (foi o que fez a primeira versão deste conversor virar no-op silencioso).
 *
 * Limite conhecido: pseudo-elementos não recebem estilo inline, então um
 * ::before com color-mix dentro de região capturada ainda quebraria — hoje
 * não existe nenhum (EmptyState já contornou o dele com currentColor+opacity).
 */

// Propriedades cujo valor o html2canvas parseia como cor (direta ou embutida).
const PROPRIEDADES_DE_COR = [
  "color",
  "background-color",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "border-inline-start-color",
  "border-inline-end-color",
  "outline-color",
  "text-decoration-color",
  "background-image",
  "box-shadow",
  "text-shadow",
  "fill",
  "stroke",
];

const TEM_FUNCAO_MODERNA = /(?:^|[\s,(])(?:color|oklch|oklab|lab|lch|color-mix)\(/;
// Uma função de cor com até um nível de parênteses aninhados (cobre o
// `color-mix(in srgb, rgb(...) 22%, transparent)` que sobrevive em gradientes).
const CADA_FUNCAO_MODERNA = /(?:color|oklch|oklab|lab|lch|color-mix)\((?:[^()]|\([^()]*\))*\)/g;

// Sentinela improvável: se fillStyle não aceitar o valor, ele permanece e
// sabemos que a conversão falhou (aí devolvemos o original, sem piorar).
const SENTINELA = "#010203";

let contextoConversao = null;
const cacheConversao = new Map();

function converterParaLegado(cor) {
  const emCache = cacheConversao.get(cor);
  if (emCache) return emCache;

  if (!contextoConversao) {
    contextoConversao = document
      .createElement("canvas")
      .getContext("2d", { willReadFrequently: true });
    // Ambiente sem canvas 2D (jsdom): melhor devolver o original do que
    // derrubar a captura inteira por causa de uma cor.
    if (!contextoConversao) return cor;
    contextoConversao.canvas.width = 1;
    contextoConversao.canvas.height = 1;
  }

  contextoConversao.fillStyle = SENTINELA;
  try {
    contextoConversao.fillStyle = cor;
  } catch {
    return cor;
  }
  if (contextoConversao.fillStyle === SENTINELA) return cor;

  contextoConversao.clearRect(0, 0, 1, 1);
  contextoConversao.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = contextoConversao.getImageData(0, 0, 1, 1).data;
  const legado = formatarLegado(r, g, b, a);
  cacheConversao.set(cor, legado);
  return legado;
}

/**
 * Monta a cor legada a partir dos bytes lidos do canvas.
 *
 * getImageData despremultiplica com arredondamento — desvio de ±1 por canal
 * em cores translúcidas, invisível a olho.
 */
function formatarLegado(r, g, b, a) {
  return a === 255
    ? `rgb(${r}, ${g}, ${b})`
    : `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(4)})`;
}

function normalizarValor(valor, conversor = converterParaLegado) {
  return valor.replace(CADA_FUNCAO_MODERNA, (funcao) => conversor(funcao));
}

/**
 * Converte, no próprio documento vivo, toda cor moderna computada dentro de
 * `raiz` para o equivalente legado (estilo inline com !important, para vencer
 * a regra original). Devolve a função que desfaz tudo.
 *
 * Deve rodar DEPOIS do export-mode estar aplicado (menos valores para varrer:
 * box-shadow das regiões capturadas já foi zerado) e ANTES do html2canvas.
 *
 * @param {HTMLElement} raiz  elemento que será capturado
 * @returns {() => void} restaura os estilos inline originais
 */
export function normalizarCoresParaCaptura(raiz) {
  if (!raiz) return () => {};

  const janela = raiz.ownerDocument?.defaultView || window;
  const restauracoes = [];
  const elementos = [raiz, ...raiz.querySelectorAll("*")];

  for (const el of elementos) {
    let computado = null;
    for (const prop of PROPRIEDADES_DE_COR) {
      if (!computado) computado = janela.getComputedStyle(el);
      const valor = computado.getPropertyValue(prop);
      if (!valor || !TEM_FUNCAO_MODERNA.test(valor)) continue;

      const normalizado = normalizarValor(valor);
      if (normalizado === valor) continue;

      restauracoes.push([
        el,
        prop,
        el.style.getPropertyValue(prop),
        el.style.getPropertyPriority(prop),
      ]);
      el.style.setProperty(prop, normalizado, "important");
    }
  }

  return function restaurar() {
    restauracoes.forEach(([el, prop, valor, prioridade]) => {
      if (valor) {
        el.style.setProperty(prop, valor, prioridade);
      } else {
        el.style.removeProperty(prop);
      }
    });
  };
}

// Exposto para teste unitário (não usar fora daqui).
export const _interno = { converterParaLegado, normalizarValor, formatarLegado };
